import * as React from 'react'
import clsx from 'clsx'
import { Modal, ModalProps } from '../Modal'
import { Paper, PaperProps } from '../Paper'
import { TransitionProps } from '../transitions'
import { sx as sxFunc, SxProps } from '../sx'
import { Backdrop } from '../Backdrop'
import { Fade } from '../Fade'
import { useId } from '../../hooks'
import { DialogContext } from './DialogContext'
import styles from './index.module.scss'
import { capitalize } from '../../utils'

export interface DialogClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the container element if `scroll="paper"`. */
  scrollPaper: string
  /** Styles applied to the container element if `scroll="body"`. */
  scrollBody: string
  /** Styles applied to the container element. */
  container: string
  /** Styles applied to the Paper component. */
  paper: string
  /** Styles applied to the Paper component if `scroll="paper"`. */
  paperScrollPaper: string
  /** Styles applied to the Paper component if `scroll="body"`. */
  paperScrollBody: string
  /** Styles applied to the Paper component if `maxWidth=false`. */
  paperWidthFalse: string
  /** Styles applied to the Paper component if `maxWidth="xs"`. */
  paperWidthXs: string
  /** Styles applied to the Paper component if `maxWidth="sm"`. */
  paperWidthSm: string
  /** Styles applied to the Paper component if `maxWidth="md"`. */
  paperWidthMd: string
  /** Styles applied to the Paper component if `maxWidth="lg"`. */
  paperWidthLg: string
  /** Styles applied to the Paper component if `maxWidth="xl"`. */
  paperWidthXl: string
  /** Styles applied to the Paper component if `maxWidth="xl"`. */
  paperWidthXxl: string
  /** Styles applied to the Paper component if `fullWidth={true}`. */
  paperFullWidth: string
  /** Styles applied to the Paper component if `fullScreen={true}`. */
  paperFullScreen: string
}

export interface DialogProps extends Omit<ModalProps, 'children'> {
  /**
   * The id(s) of the element(s) that describe the dialog.
   */
  'aria-describedby'?: string
  /**
   * The id(s) of the element(s) that label the dialog.
   */
  'aria-labelledby'?: string
  /**
   * Dialog children, usually the included sub-components.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<DialogClasses>
  /**
   * If `true`, hitting escape will not fire the `onClose` callback.
   * @default false
   */
  disableEscapeKeyDown?: boolean
  /**
   * If `true`, the dialog is full-screen.
   * @default false
   */
  fullScreen?: boolean
  /**
   * If `true`, the dialog stretches to `maxWidth`.
   *
   * Notice that the dialog width grow is limited by the default margin.
   * @default false
   */
  fullWidth?: boolean
  /**
   * Determine the max-width of the dialog.
   * The dialog width grows with the size of the screen.
   * Set to `false` to disable `maxWidth`.
   * @default 'sm'
   */
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | false
  /**
   * Callback fired when the component requests to be closed.
   *
   * @param {object} event The event source of the callback.
   * @param {string} reason Can be: `"escapeKeyDown"`, `"backdropClick"`.
   */
  onClose?: ModalProps['onClose']
  /**
   * If `true`, the component is shown.
   */
  open: ModalProps['open']
  /**
   * The component used to render the body of the dialog.
   * @default Paper
   */
  PaperComponent?: React.JSXElementConstructor<PaperProps>
  /**
   * Props applied to the [`Paper`](/material-ui/api/paper/) element.
   * @default {}
   */
  PaperProps?: Partial<PaperProps>
  /**
   * Determine the container for scrolling the dialog.
   * @default 'paper'
   */
  scroll?: 'body' | 'paper'
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * The component used for the transition.
   * [Follow this guide](/material-ui/transitions/#transitioncomponent-prop) to learn more about the requirements for this component.
   * @default Fade
   */
  TransitionComponent?: React.JSXElementConstructor<
    TransitionProps & { children: React.ReactElement<any, any> }
  >
  /**
   * The duration for the transition, in milliseconds.
   * You may specify a single timeout for all transitions, or individually with an object.
   * @default {
   *   enter: theme.transitions.duration.enteringScreen,
   *   exit: theme.transitions.duration.leavingScreen,
   * }
   */
  transitionDuration?: TransitionProps['timeout']
  /**
   * Props applied to the transition element.
   * By default, the element is based on this [`Transition`](http://reactcommunity.org/react-transition-group/transition/) component.
   */
  TransitionProps?: TransitionProps
}

/**
 * Dialogs are overlaid modal paper based components with a backdrop.
 */
export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
  function Dialog(props, ref) {
    const defaultTransitionDuration = {
      enter: 255, // refer to css variable --transitionDurationEnteringScreen
      exit: 195, // refer to css variable --transitionDurationLeavingScreen
    }
    const {
      'aria-describedby': ariaDescribedby,
      'aria-labelledby': ariaLabelledbyProp,
      children,
      classes,
      className,
      disableEscapeKeyDown = false,
      fullScreen = false,
      fullWidth = false,
      maxWidth = 'sm',
      onClose,
      open,
      PaperComponent = Paper,
      PaperProps = {},
      scroll = 'paper',
      TransitionComponent = Fade,
      transitionDuration = defaultTransitionDuration,
      TransitionProps,
      sx,
      ...other
    } = props

    const backdropClick = React.useRef<boolean | null>()
    const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
      // We don't want to close the dialog when clicking the dialog content.
      // Make sure the event starts and ends on the same DOM element.
      backdropClick.current = event.target === event.currentTarget
    }
    const handleBackdropClick = (event: React.MouseEvent<HTMLDivElement>) => {
      // Ignore the events not coming from the "backdrop".
      if (!backdropClick.current) {
        return
      }

      backdropClick.current = null

      if (onClose) {
        onClose(event, 'backdropClick')
      }
    }

    const ariaLabelledby = useId(ariaLabelledbyProp)
    const dialogContextValue = React.useMemo(() => {
      return { titleId: ariaLabelledby }
    }, [ariaLabelledby])

    const Container = 'div'

    return (
      <Modal
        className={clsx(styles.root, classes?.root, className, sxFunc(sx))}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            className: styles.backdrop,
            // @ts-ignore
            transitionDuration,
          },
        }}
        disableEscapeKeyDown={disableEscapeKeyDown}
        onClose={onClose}
        open={open}
        ref={ref}
        onClick={handleBackdropClick}
        {...other}
      >
        <TransitionComponent
          appear
          in={open}
          timeout={transitionDuration}
          role="presentation"
          {...TransitionProps}
        >
          {/* roles are applied via cloneElement from TransitionComponent */}
          {/* roles needs to be applied on the immediate child of Modal or it'll inject one */}
          <Container
            className={clsx(
              styles.container,
              styles[`scroll-${scroll}`],
              // @ts-ignore
              classes?.[`scroll${capitalize(scroll)}`],
              classes?.container
            )}
            onMouseDown={handleMouseDown}
          >
            <PaperComponent
              elevation={3}
              role="dialog"
              aria-describedby={ariaDescribedby}
              aria-labelledby={ariaLabelledby}
              {...PaperProps}
              className={clsx(
                styles.paper,
                styles[`scroll-${scroll}`],
                // @ts-ignore
                classes?.[`scroll${capitalize(scroll)}`],
                (fullWidth || maxWidth === false) && [
                  styles.fullWidth,
                  classes?.paperFullWidth,
                ],
                fullScreen && [styles.fullScreen, classes?.paperFullScreen],
                maxWidth && [
                  styles[`maxWidth-${maxWidth}`],
                  // @ts-ignore
                  classes?.[`paperWidth${capitalize(maxWidth)}`],
                ],
                scroll === 'paper' && [
                  styles['paperScroll-body'],
                  classes?.paperScrollBody,
                ],
                classes?.paper,
                PaperProps.className
              )}
            >
              <DialogContext.Provider value={dialogContextValue}>
                {children}
              </DialogContext.Provider>
            </PaperComponent>
          </Container>
        </TransitionComponent>
      </Modal>
    )
  }
)
